/**
 * PlayerProfileModal
 *
 * Generic full-stat player profile, opened by clicking a player anywhere in
 * the app. First wired up from Opponent Scout's Key Threats (which only ever
 * has a `season` window to show), but built sport/position-generic so
 * Waiver Wire, Trade Analyser, and Team Analyser can open the same component
 * later with their own `stats` — same canonical window shape
 * playerAnalyserService.js's buildNbaSeasonWindow/buildNflStatWindow and
 * teamAnalyserService.js's topPerformerForResponse already produce
 * (season/last7/last30/priorSeason, each omitted when absent).
 */

import ModalPortal, { ModalHeader, ModalFooter, GhostButton } from './ModalPortal'
import PlayerPhoto from './PlayerPhoto'
import StatTable from './StatTable'
import TeamChip from './TeamChip'
import { V2, StatusPill, PosChip } from './JaiV2'
import { statColumnsFor } from '../config/statColumns'

// Same window order/labels PlayerAnalyser.jsx's stat table uses — one
// canonical mapping for "which windows exist and what they're called."
const ROW_ORDER = [
  ['season', 'Season'],
  ['last7', 'Last 7 Games'],
  ['last30', 'Last 30 Games'],
  ['priorSeason', 'Last Season'],
]

function buildStatRows(stats, columns, sport) {
  if (!stats) return []
  return ROW_ORDER
    .filter(([key]) => stats[key])
    .map(([key, label]) => {
      const values = {}
      columns.forEach((c) => { values[c.key] = stats[key][c.key] })
      // NFL fantasy points is a per-game rate with no implicit window size —
      // show games played next to the label so it can't be misread as a
      // bigger sample than it is.
      const rowLabel = sport === 'nfl' && stats[key].gamesPlayed != null
        ? `${label} (${stats[key].gamesPlayed} gp)`
        : label
      return { label: rowLabel, values }
    })
}

/**
 * @param {object} player       {name, position, team, injuryStatus, playerId}
 * @param {'nba'|'nfl'} sport
 * @param {object} stats        canonical window shape — {season, last7, last30, priorSeason}, any subset
 * @param {() => void} onClose
 */
export default function PlayerProfileModal({ player, sport = 'nba', stats, onClose }) {
  if (!player) return null

  const columns = statColumnsFor(sport, player.position)
  const statRows = buildStatRows(stats, columns, sport)
  const titleId = 'player-profile-modal-title'

  return (
    <ModalPortal onClose={onClose} labelledBy={titleId} width={480}>
      <ModalHeader title="Player profile" titleId={titleId} onClose={onClose} />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <PlayerPhoto playerId={player.playerId} name={player.name} size={52} rounded="full" />
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 18, fontWeight: 800, color: V2.navy, letterSpacing: '-0.02em' }}>{player.name}</span>
              {player.injuryStatus && player.injuryStatus !== 'ACTIVE' && <StatusPill status={player.injuryStatus} />}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              {player.position && <PosChip>{player.position}</PosChip>}
              {player.team && <TeamChip abbr={player.team} sport={sport} />}
            </div>
          </div>
        </div>

        {statRows.length > 0 ? (
          <StatTable title="Season stats" columns={columns} rows={statRows} />
        ) : (
          <p style={{ fontSize: 13, color: V2.faint, margin: 0 }}>No season stats available for this player yet.</p>
        )}
      </div>

      <ModalFooter>
        <GhostButton onClick={onClose}>Close</GhostButton>
      </ModalFooter>
    </ModalPortal>
  )
}
